import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  Snackbar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import useHomePageContent, { HomePageContent } from '../../hooks/useHomePageContent';
import defaultContent from '../../config/homePageContent.json';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`content-tabpanel-${index}`}
      aria-labelledby={`content-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ContentEditorPage: React.FC = () => {
  const { content: initialContent } = useHomePageContent();
  const [content, setContent] = useState<HomePageContent | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [jsonContent, setJsonContent] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setContent(initialContent);
    setJsonContent(JSON.stringify(initialContent, null, 2));
  }, [initialContent]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1) {
      // Update JSON tab with current form values
      setJsonContent(JSON.stringify(content, null, 2));
    }
  };

  const handleContentChange = (section: string, field: string, value: string) => {
    if (!content) return;
    
    const newContent = { ...content };
    // Handle nested paths like "heroSection.title"
    const path = `${section}.${field}`.split('.');
    let current: any = newContent;
    
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    
    current[path[path.length - 1]] = value;
    setContent(newContent);
  };

  const handleArrayChange = (section: string, index: number, field: string, value: string) => {
    if (!content) return;

    const newContent = { ...content };
    const sectionParts = section.split('.');
    
    let current: any = newContent;
    for (const part of sectionParts) {
      current = current[part];
    }
    
    current[index][field] = value;
    setContent(newContent);
  };

  const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonContent(e.target.value);
    try {
      const parsed = JSON.parse(e.target.value);
      setContent(parsed);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Invalid JSON format');
    }
  };

  const handleSaveContent = () => {
    try {
      // In a real implementation, this would save to a server
      // For now, we just show a success message
      localStorage.setItem('homePageContent', JSON.stringify(content));
      setSuccessMessage('Content saved! In a real implementation, this would save to the server.');
      
      // Create a download link with the content
      const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'homePageContent.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMessage('Failed to save content');
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(jsonContent)
      .then(() => {
        setSuccessMessage('Content copied to clipboard!');
      })
      .catch(() => {
        setErrorMessage('Failed to copy to clipboard');
      });
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  if (!content) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>Loading content editor...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h4" gutterBottom>Homepage Content Editor</Typography>
        <Typography variant="body1" paragraph>
          Use this page to customize the text on the homepage. Changes will be saved as a JSON file you can download and replace in your config folder.
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          After making changes, click "Save & Download JSON" to download the updated content file. 
          Replace the file at <code>src/config/homePageContent.json</code> with this downloaded file.
        </Alert>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="content editor tabs">
          <Tab label="Form Editor" />
          <Tab label="JSON Editor" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 4 }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SaveIcon />}
            onClick={handleSaveContent}
            sx={{ mr: 2 }}
          >
            Save & Download JSON
          </Button>
        </Box>

        {/* Hero Section */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="hero-section-content"
            id="hero-section-header"
          >
            <Typography variant="h6">Hero Section</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Title"
                fullWidth
                value={content.heroSection.title}
                onChange={(e) => handleContentChange('heroSection', 'title', e.target.value)}
              />
              <TextField
                label="Subtitle"
                fullWidth
                value={content.heroSection.subtitle}
                onChange={(e) => handleContentChange('heroSection', 'subtitle', e.target.value)}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Get Started Button"
                  fullWidth
                  value={content.heroSection.getStartedButton}
                  onChange={(e) => handleContentChange('heroSection', 'getStartedButton', e.target.value)}
                />
                <TextField
                  label="Login Button"
                  fullWidth
                  value={content.heroSection.loginButton}
                  onChange={(e) => handleContentChange('heroSection', 'loginButton', e.target.value)}
                />
                <TextField
                  label="Dashboard Button"
                  fullWidth
                  value={content.heroSection.dashboardButton}
                  onChange={(e) => handleContentChange('heroSection', 'dashboardButton', e.target.value)}
                />
              </Box>
              <TextField
                label="Gift Card Value"
                value={content.heroSection.giftCardValue}
                onChange={(e) => handleContentChange('heroSection', 'giftCardValue', e.target.value)}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Quick Access Section */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="quick-access-section-content"
            id="quick-access-section-header"
          >
            <Typography variant="h6">Quick Card Services Section</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Section Title"
                fullWidth
                value={content.quickAccessSection.title}
                onChange={(e) => handleContentChange('quickAccessSection', 'title', e.target.value)}
              />
              <TextField
                label="Section Description"
                fullWidth
                value={content.quickAccessSection.description}
                onChange={(e) => handleContentChange('quickAccessSection', 'description', e.target.value)}
              />
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1">Activate Card</Typography>
              
              <TextField
                label="Title"
                fullWidth
                value={content.quickAccessSection.activateCard.title}
                onChange={(e) => handleContentChange('quickAccessSection.activateCard', 'title', e.target.value)}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={content.quickAccessSection.activateCard.description}
                onChange={(e) => handleContentChange('quickAccessSection.activateCard', 'description', e.target.value)}
              />
              
              <Box sx={{ pl: 2, borderLeft: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Business Activation</Typography>
                <TextField
                  label="Title"
                  fullWidth
                  value={content.quickAccessSection.activateCard.business.title}
                  onChange={(e) => handleContentChange('quickAccessSection.activateCard.business', 'title', e.target.value)}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Description"
                  fullWidth
                  value={content.quickAccessSection.activateCard.business.description}
                  onChange={(e) => handleContentChange('quickAccessSection.activateCard.business', 'description', e.target.value)}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Button Text"
                  value={content.quickAccessSection.activateCard.business.buttonText}
                  onChange={(e) => handleContentChange('quickAccessSection.activateCard.business', 'buttonText', e.target.value)}
                />
              </Box>
              
              <Box sx={{ pl: 2, borderLeft: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Customer Activation</Typography>
                <TextField
                  label="Title"
                  fullWidth
                  value={content.quickAccessSection.activateCard.customer.title}
                  onChange={(e) => handleContentChange('quickAccessSection.activateCard.customer', 'title', e.target.value)}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Description"
                  fullWidth
                  value={content.quickAccessSection.activateCard.customer.description}
                  onChange={(e) => handleContentChange('quickAccessSection.activateCard.customer', 'description', e.target.value)}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Button Text"
                  value={content.quickAccessSection.activateCard.customer.buttonText}
                  onChange={(e) => handleContentChange('quickAccessSection.activateCard.customer', 'buttonText', e.target.value)}
                />
              </Box>
              
              <Box sx={{ pl: 2, borderLeft: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>QR Code</Typography>
                <TextField
                  label="Description"
                  fullWidth
                  value={content.quickAccessSection.activateCard.qrCode.description}
                  onChange={(e) => handleContentChange('quickAccessSection.activateCard.qrCode', 'description', e.target.value)}
                />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1">Check Balance Card</Typography>
              
              <TextField
                label="Title"
                fullWidth
                value={content.quickAccessSection.checkBalance.title}
                onChange={(e) => handleContentChange('quickAccessSection.checkBalance', 'title', e.target.value)}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={content.quickAccessSection.checkBalance.description}
                onChange={(e) => handleContentChange('quickAccessSection.checkBalance', 'description', e.target.value)}
              />
              <TextField
                label="Button Text"
                value={content.quickAccessSection.checkBalance.buttonText}
                onChange={(e) => handleContentChange('quickAccessSection.checkBalance', 'buttonText', e.target.value)}
              />
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1">Register Card</Typography>
              
              <TextField
                label="Title"
                fullWidth
                value={content.quickAccessSection.registerCard.title}
                onChange={(e) => handleContentChange('quickAccessSection.registerCard', 'title', e.target.value)}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={content.quickAccessSection.registerCard.description}
                onChange={(e) => handleContentChange('quickAccessSection.registerCard', 'description', e.target.value)}
              />
              <TextField
                label="Button Text"
                value={content.quickAccessSection.registerCard.buttonText}
                onChange={(e) => handleContentChange('quickAccessSection.registerCard', 'buttonText', e.target.value)}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Network Section */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="network-section-content"
            id="network-section-header"
          >
            <Typography variant="h6">Business Network Section</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Title"
                fullWidth
                value={content.networkSection.title}
                onChange={(e) => handleContentChange('networkSection', 'title', e.target.value)}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={content.networkSection.description}
                onChange={(e) => handleContentChange('networkSection', 'description', e.target.value)}
              />
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1">Highlight Box</Typography>
              
              <TextField
                label="Title"
                fullWidth
                value={content.networkSection.highlight.title}
                onChange={(e) => handleContentChange('networkSection.highlight', 'title', e.target.value)}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={content.networkSection.highlight.description}
                onChange={(e) => handleContentChange('networkSection.highlight', 'description', e.target.value)}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Number"
                  value={content.networkSection.highlight.numberText}
                  onChange={(e) => handleContentChange('networkSection.highlight', 'numberText', e.target.value)}
                />
                <TextField
                  label="Label"
                  value={content.networkSection.highlight.numberLabel}
                  onChange={(e) => handleContentChange('networkSection.highlight', 'numberLabel', e.target.value)}
                />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1">Feature Points</Typography>
              
              {content.networkSection.points.map((point, index) => (
                <Box key={index} sx={{ pl: 2, pb: 2, mb: 2, borderLeft: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Point {index + 1}</Typography>
                  <TextField
                    label="Title"
                    fullWidth
                    value={point.title}
                    onChange={(e) => handleArrayChange('networkSection.points', index, 'title', e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={point.description}
                    onChange={(e) => handleArrayChange('networkSection.points', index, 'description', e.target.value)}
                  />
                </Box>
              ))}
              
              <TextField
                label="Button Text"
                value={content.networkSection.buttonText}
                onChange={(e) => handleContentChange('networkSection', 'buttonText', e.target.value)}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Features Section */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="features-section-content"
            id="features-section-header"
          >
            <Typography variant="h6">Who We Serve Section</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Title"
                fullWidth
                value={content.featuresSection.title}
                onChange={(e) => handleContentChange('featuresSection', 'title', e.target.value)}
              />
              <TextField
                label="Description"
                fullWidth
                value={content.featuresSection.description}
                onChange={(e) => handleContentChange('featuresSection', 'description', e.target.value)}
              />
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1">Roles</Typography>
              
              {content.featuresSection.roles.map((role, index) => (
                <Box key={index} sx={{ pl: 2, pb: 2, mb: 2, borderLeft: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{role.title}</Typography>
                  <TextField
                    label="Title"
                    fullWidth
                    value={role.title}
                    onChange={(e) => handleArrayChange('featuresSection.roles', index, 'title', e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={role.description}
                    onChange={(e) => handleArrayChange('featuresSection.roles', index, 'description', e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label="Button Text"
                    value={role.buttonText}
                    onChange={(e) => handleArrayChange('featuresSection.roles', index, 'buttonText', e.target.value)}
                  />
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* How It Works Section */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="how-it-works-section-content"
            id="how-it-works-section-header"
          >
            <Typography variant="h6">How It Works Section</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Title"
                fullWidth
                value={content.howItWorksSection.title}
                onChange={(e) => handleContentChange('howItWorksSection', 'title', e.target.value)}
              />
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1">Steps</Typography>
              
              {content.howItWorksSection.steps.map((step, index) => (
                <Box key={index} sx={{ pl: 2, pb: 2, mb: 2, borderLeft: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Step {step.number}</Typography>
                  <TextField
                    label="Step Number"
                    type="number"
                    value={step.number}
                    onChange={(e) => handleArrayChange('howItWorksSection.steps', index, 'number', e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label="Title"
                    fullWidth
                    value={step.title}
                    onChange={(e) => handleArrayChange('howItWorksSection.steps', index, 'title', e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={step.description}
                    onChange={(e) => handleArrayChange('howItWorksSection.steps', index, 'description', e.target.value)}
                  />
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* CTA Section */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="cta-section-content"
            id="cta-section-header"
          >
            <Typography variant="h6">Call-to-Action Section</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Title"
                fullWidth
                value={content.ctaSection.title}
                onChange={(e) => handleContentChange('ctaSection', 'title', e.target.value)}
              />
              <TextField
                label="Description"
                fullWidth
                value={content.ctaSection.description}
                onChange={(e) => handleContentChange('ctaSection', 'description', e.target.value)}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Register Button"
                  fullWidth
                  value={content.ctaSection.registerButton}
                  onChange={(e) => handleContentChange('ctaSection', 'registerButton', e.target.value)}
                />
                <TextField
                  label="Login Button"
                  fullWidth
                  value={content.ctaSection.loginButton}
                  onChange={(e) => handleContentChange('ctaSection', 'loginButton', e.target.value)}
                />
                <TextField
                  label="Dashboard Button"
                  fullWidth
                  value={content.ctaSection.dashboardButton}
                  onChange={(e) => handleContentChange('ctaSection', 'dashboardButton', e.target.value)}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 4 }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SaveIcon />}
            onClick={handleSaveContent}
            sx={{ mr: 2 }}
          >
            Save & Download JSON
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyToClipboard}
          >
            Copy to Clipboard
          </Button>
        </Box>
        <TextField
          label="JSON Content"
          multiline
          rows={30}
          fullWidth
          value={jsonContent}
          onChange={handleJsonChange}
          variant="outlined"
          error={!!errorMessage}
          helperText={errorMessage || "Edit the JSON directly. Be careful to maintain valid JSON format."}
        />
      </TabPanel>

      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContentEditorPage; 